import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home as HomeIcon, Heart, Trophy, User, PartyPopper, Newspaper,
  Filter, ChevronDown, ArrowUpDown, CheckCircle, Gift, Compass,
  Shield, AlertTriangle, Smartphone, DollarSign, Star,
  Utensils, Sandwich, Coffee, CakeSlice, IceCreamCone,
  Shirt, ShoppingBag, Store, Ticket, Gem,
  PawPrint, Sparkles, Leaf, Clock, X,
  Repeat, MapPin, Baby, Percent, Wrench,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { API, axios } from "@/api";

const catConfig = {
  "Restaurant":              { icon: Utensils,     bg: "bg-orange-100", ic: "text-orange-600", tx: "text-orange-600", ck: "text-orange-500" },
  "Fast Food":               { icon: Sandwich,     bg: "bg-red-100",    ic: "text-red-600",    tx: "text-red-600",    ck: "text-red-500" },
  "Cafes & Drinks":          { icon: Coffee,       bg: "bg-amber-100",  ic: "text-amber-700",  tx: "text-amber-700",  ck: "text-amber-600" },
  "Bakery":                  { icon: CakeSlice,    bg: "bg-pink-100",   ic: "text-pink-600",   tx: "text-pink-600",   ck: "text-pink-500" },
  "Dessert & Ice Cream":     { icon: IceCreamCone, bg: "bg-rose-100",   ic: "text-rose-500",   tx: "text-rose-600",   ck: "text-rose-500" },
  "Beauty & Skincare":       { icon: Gem,          bg: "bg-pink-100",   ic: "text-pink-500",   tx: "text-pink-600",   ck: "text-pink-500" },
  "Clothing & Apparel":      { icon: Shirt,        bg: "bg-indigo-100", ic: "text-indigo-600", tx: "text-indigo-600", ck: "text-indigo-500" },
  "Jewelry & Accessories":   { icon: Gem,          bg: "bg-amber-100",  ic: "text-amber-600",  tx: "text-amber-600",  ck: "text-amber-500" },
  "Retail":                  { icon: ShoppingBag,  bg: "bg-violet-100", ic: "text-violet-600", tx: "text-violet-600", ck: "text-violet-500" },
  "Grocery":                 { icon: ShoppingBag,  bg: "bg-emerald-100",ic: "text-emerald-600",tx: "text-emerald-600",ck: "text-emerald-500" },
  "Entertainment":           { icon: Ticket,       bg: "bg-purple-100", ic: "text-purple-600", tx: "text-purple-600", ck: "text-purple-500" },
  "Pet":                     { icon: PawPrint,     bg: "bg-lime-100",   ic: "text-lime-600",   tx: "text-lime-600",   ck: "text-lime-500" },
  "Health & Fitness":        { icon: Sparkles,     bg: "bg-sky-100",    ic: "text-sky-600",    tx: "text-sky-600",    ck: "text-sky-500" },
  "Auto":                    { icon: Wrench,       bg: "bg-slate-100",  ic: "text-slate-600",  tx: "text-slate-600",  ck: "text-slate-500" },
  "Home & Garden":           { icon: Store,        bg: "bg-emerald-100",ic: "text-emerald-600",tx: "text-emerald-600",ck: "text-emerald-500" },
  "Other":                   { icon: Gift,         bg: "bg-slate-100",  ic: "text-slate-500",  tx: "text-slate-600",  ck: "text-slate-500" },
};
const defaultCatCfg = { icon: Gift, bg: "bg-slate-100", ic: "text-slate-500", tx: "text-slate-600", ck: "text-slate-500" };

/* ──────────────────────────────────────────────
   UNIFIED FILTER SHEET
   Dietary filters → Offer filters → Mode cards
   ────────────────────────────────────────────── */
export const UnifiedFilterSheet = ({
  categories, selectedCategory, onSelectCategory,
  sortBy, onSelectSort,
  halalFilter, setHalalFilter,
  halalCertifiedFilter, setHalalCertifiedFilter,
  veganFilter, setVeganFilter,
  vegetarianFilter, setVegetarianFilter,
  glutenFreeFilter, setGlutenFreeFilter,
  boycottFilter, setBoycottFilter, noAppFilter, setNoAppFilter,
  highValueFilter, setHighValueFilter, easyClaimFilter, setEasyClaimFilter,
  verifiedFilter, setVerifiedFilter, windowFilter, setWindowFilter,
  trueFreebieFilter, setTrueFreebieFilter,
  bogoFilter, setBogoFilter,
  rewardTypeFilter, setRewardTypeFilter,
  inStoreFilter, setInStoreFilter,
  kidsFilter, setKidsFilter,
  hasDietaryFilter, showBoycott = false,
  saveDietaryDefaults, dietaryDiffersFromDefaults,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const { mode, theme } = useTheme();
  const isDiscover = mode === "discover";

  const dietaryCount = [halalFilter, halalCertifiedFilter, veganFilter, vegetarianFilter, glutenFreeFilter].filter(Boolean).length;
  const offerCount = [boycottFilter, noAppFilter, highValueFilter, easyClaimFilter, verifiedFilter, trueFreebieFilter, bogoFilter, inStoreFilter, kidsFilter, !!windowFilter, !!rewardTypeFilter].filter(Boolean).length;
  const totalActive = dietaryCount + (isDiscover ? 0 : offerCount + selectedCategory.length + (sortBy !== "urgency" ? 1 : 0));

  const sortOptions = [
    { value: "urgency", label: "Urgency" },
    { value: "value", label: "Highest Value" },
    { value: "ease", label: "Easiest" },
    { value: "name", label: "A-Z" },
  ];

  const offerFilters = [
    { key: "trueFreebie", label: "True Freebie", icon: Gift, active: trueFreebieFilter, toggle: () => setTrueFreebieFilter(!trueFreebieFilter), bg: "bg-teal-100", ic: "text-teal-600", tx: "text-teal-700", ck: "text-teal-500", testId: "filter-true-freebie" },
    { key: "noApp", label: "No App Needed", icon: Smartphone, active: noAppFilter, toggle: () => setNoAppFilter(!noAppFilter), bg: "bg-purple-100", ic: "text-purple-600", tx: "text-purple-700", ck: "text-purple-500", testId: "filter-no-app" },
    { key: "highValue", label: "High Value ($10+)", icon: DollarSign, active: highValueFilter, toggle: () => setHighValueFilter(!highValueFilter), bg: "bg-emerald-100", ic: "text-emerald-600", tx: "text-emerald-700", ck: "text-emerald-500", testId: "filter-high-value" },
    { key: "easyClaim", label: "Easy to Claim (4+)", icon: Star, active: easyClaimFilter, toggle: () => setEasyClaimFilter(!easyClaimFilter), bg: "bg-amber-100", ic: "text-amber-600", tx: "text-amber-700", ck: "text-amber-500", testId: "filter-easy-claim" },
    { key: "verified", label: "Verified Only", icon: CheckCircle, active: verifiedFilter, toggle: () => setVerifiedFilter(!verifiedFilter), bg: "bg-sky-100", ic: "text-sky-600", tx: "text-sky-700", ck: "text-sky-500", testId: "filter-verified" },
    { key: "bogo", label: "BOGO", icon: Repeat, active: bogoFilter, toggle: () => setBogoFilter(!bogoFilter), bg: "bg-orange-100", ic: "text-orange-600", tx: "text-orange-700", ck: "text-orange-500", testId: "filter-bogo" },
    { key: "inStore", label: "In-Store Only", icon: MapPin, active: inStoreFilter, toggle: () => setInStoreFilter(!inStoreFilter), bg: "bg-indigo-100", ic: "text-indigo-600", tx: "text-indigo-700", ck: "text-indigo-500", testId: "filter-in-store" },
    { key: "kids", label: "Kids & Family", icon: Baby, active: kidsFilter, toggle: () => setKidsFilter(!kidsFilter), bg: "bg-pink-100", ic: "text-pink-600", tx: "text-pink-700", ck: "text-pink-500", testId: "filter-kids" },
  ];

  const windowOptions = [
    { value: "1", label: "Birthday Only" }, { value: "7", label: "7 days" },
    { value: "14", label: "14 days" }, { value: "30", label: "30 days" },
  ];

  const rewardTypeOptions = [
    { value: "", label: "Any" }, { value: "free_dessert", label: "Free Dessert" },
    { value: "free_meal", label: "Free Meal" }, { value: "percentage_off", label: "% Off" },
    { value: "bogo", label: "BOGO" },
  ];

  const clearAll = () => {
    setHalalFilter(false); setHalalCertifiedFilter?.(false);
    setVeganFilter(false); setVegetarianFilter(false);
    setGlutenFreeFilter?.(false);
    setBoycottFilter(false); setNoAppFilter(false);
    setHighValueFilter(false); setEasyClaimFilter(false);
    setVerifiedFilter(false); setTrueFreebieFilter(false);
    setWindowFilter?.(""); setBogoFilter(false);
    setInStoreFilter(false); setKidsFilter(false);
    setRewardTypeFilter?.(""); onSelectCategory([]); onSelectSort("urgency");
  };

  const toggleCategory = (cat) => {
    if (cat === "All") return onSelectCategory([]);
    const next = selectedCategory.includes(cat) ? selectedCategory.filter(c => c !== cat) : [...selectedCategory, cat];
    onSelectCategory(next);
  };

  const activeNames = [];
  if (halalCertifiedFilter) activeNames.push("Halal Certified");
  else if (halalFilter) activeNames.push("Halal");
  if (veganFilter) activeNames.push("Vegan");
  if (vegetarianFilter) activeNames.push("Vegetarian");
  const resultLabel = activeNames.length > 0
    ? `Showing: ${activeNames.join(", ")} ${isDiscover ? "places" : "birthday offers"} near you`
    : isDiscover ? "Select at least one dietary filter" : "Showing: All birthday offers near you";

  return (
    <>
      <button onClick={() => setIsOpen(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-xl border shadow-sm text-sm font-medium transition-all" style={totalActive > 0 ? { backgroundColor: theme.filterActiveBg, color: theme.filterActive, borderColor: theme.filterActiveBorder } : { backgroundColor: "#fff", color: "#475569", borderColor: "#e2e8f0" }} data-testid="filters-dropdown">
        <Filter className="w-4 h-4" />
        <span>Filters</span>
        {totalActive > 0 && <span className="w-5 h-5 rounded-full text-white text-[10px] flex items-center justify-center font-bold" style={{ backgroundColor: theme.primary }}>{totalActive}</span>}
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/40 z-[200]" onClick={() => setIsOpen(false)} />
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 28, stiffness: 300 }} className="fixed bottom-0 left-0 right-0 z-[210] bg-white rounded-t-3xl max-h-[90vh] flex flex-col" data-testid="filters-sheet">
              <div className="flex justify-center pt-3 pb-1"><div className="w-10 h-1 rounded-full bg-slate-300" /></div>
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
                <h3 className="text-base font-bold text-slate-800">Filters</h3>
                <div className="flex items-center gap-3">
                  {totalActive > 0 && <button onClick={clearAll} className="text-xs text-slate-400 hover:text-slate-600 font-medium" data-testid="filter-clear-all">Clear all</button>}
                  <button onClick={() => setIsOpen(false)} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center" data-testid="filters-done"><X className="w-4 h-4 text-slate-500" /></button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">

                {/* ── DIETARY FILTERS (both modes) ── */}
                <div>
                  <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider px-1 mb-2">{isDiscover ? "Select at least one dietary filter" : "Dietary filters (optional)"}</p>
                  {isDiscover && dietaryCount === 0 && (
                    <div className="mb-2 px-2 py-2 bg-indigo-50 border border-indigo-200 rounded-xl" data-testid="discover-dietary-prompt">
                      <p className="text-[11px] text-indigo-700 font-medium">Discover mode shows all places — pick a dietary filter to start browsing.</p>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => { setHalalCertifiedFilter(!halalCertifiedFilter); if (!halalCertifiedFilter) setHalalFilter(false); }} className={`px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${halalCertifiedFilter ? "bg-green-500 text-white" : "bg-green-50 text-green-700 border border-green-200"}`} data-testid="filter-halal-certified">Halal Certified</button>
                    <button onClick={() => { setHalalFilter(!halalFilter); if (!halalFilter) setHalalCertifiedFilter(false); }} className={`px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${halalFilter ? "bg-green-400 text-white" : "bg-green-50/70 text-green-600 border border-green-200/70"}`} data-testid="filter-halal-friendly">Halal Friendly</button>
                    <button onClick={() => setVeganFilter(!veganFilter)} className={`px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${veganFilter ? "bg-lime-500 text-white" : "bg-lime-50 text-lime-700 border border-lime-200"}`} data-testid="filter-vegan">Vegan</button>
                    <button onClick={() => setVegetarianFilter(!vegetarianFilter)} className={`px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${vegetarianFilter ? "bg-emerald-500 text-white" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`} data-testid="filter-vegetarian">Vegetarian</button>
                    <button onClick={() => setGlutenFreeFilter(!glutenFreeFilter)} className={`px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${glutenFreeFilter ? "bg-amber-500 text-white" : "bg-amber-50 text-amber-700 border border-amber-200"}`} data-testid="filter-gluten-free">Gluten Free</button>
                    {showBoycott && <button onClick={() => setBoycottFilter(!boycottFilter)} className={`px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${boycottFilter ? "bg-red-500 text-white" : "bg-red-50 text-red-600 border border-red-200"}`} data-testid="filter-boycott">Exclude Boycott</button>}
                  </div>
                </div>

                {/* ── OFFER FILTERS (Birthday only, grayed in Explore) ── */}
                <div className={isDiscover ? "opacity-40 pointer-events-none" : ""}>
                  <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider px-1 mb-1.5">Offer Filters</p>
                  {isDiscover && <p className="text-[10px] text-slate-400 italic px-1 mb-2" data-testid="discover-filter-notice">These filters apply to birthday offers only. Switch to Birthday mode to use them.</p>}
                  <div className="space-y-1.5">
                    {offerFilters.map((f) => { const Icon = f.icon; return (
                      <button key={f.key} onClick={f.toggle} className={`w-full px-3 py-2 text-left flex items-center justify-between rounded-xl transition-colors ${f.active ? `${f.bg} ring-1 ring-inset ring-current/10` : "bg-slate-50 hover:bg-slate-100"}`} data-testid={f.testId}>
                        <div className="flex items-center gap-2.5">
                          <span className={`w-7 h-7 rounded-lg ${f.bg} flex items-center justify-center flex-shrink-0`}><Icon className={`w-3.5 h-3.5 ${f.ic}`} /></span>
                          <span className={`text-sm font-semibold ${f.active ? f.tx : "text-slate-700"}`}>{f.label}</span>
                        </div>
                        {f.active && <CheckCircle className={`w-4 h-4 ${f.ck}`} />}
                      </button>
                    ); })}
                  </div>
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    <div className="w-full"><p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Window</p></div>
                    {windowOptions.map((wo) => <button key={wo.value} onClick={() => setWindowFilter?.(windowFilter === wo.value ? "" : wo.value)} className={`px-2.5 py-1.5 rounded-lg text-[11px] font-semibold ${windowFilter === wo.value ? "bg-sky-500 text-white" : "bg-slate-100 text-slate-600"}`} data-testid={`filter-window-${wo.value}`}>{wo.label}</button>)}
                  </div>
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    <div className="w-full"><p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Reward Type</p></div>
                    {rewardTypeOptions.map((rt) => <button key={rt.value} onClick={() => setRewardTypeFilter?.(rewardTypeFilter === rt.value ? "" : rt.value)} className={`px-2.5 py-1.5 rounded-lg text-[11px] font-semibold ${rewardTypeFilter === rt.value ? "bg-sky-500 text-white" : "bg-slate-100 text-slate-600"}`} data-testid={`filter-reward-${rt.value || "any"}`}>{rt.label}</button>)}
                  </div>
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    <div className="w-full"><p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Categories</p></div>
                    <button onClick={() => onSelectCategory([])} className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold ${selectedCategory.length === 0 ? "bg-sky-500 text-white" : "bg-slate-100 text-slate-600"}`} data-testid="category-option-all"><Gift className="w-3 h-3" /> All</button>
                    {(categories || []).filter(c => c !== "All").map((cat) => {
                      const checked = selectedCategory.includes(cat);
                      const cfg = catConfig[cat] || defaultCatCfg;
                      const Icon = cfg.icon;
                      return <button key={cat} onClick={() => toggleCategory(cat)} className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold ${checked ? `${cfg.bg} ${cfg.tx}` : "bg-slate-100 text-slate-600"}`} data-testid={`category-option-${cat.toLowerCase().replace(/\s+/g, '-')}`}><Icon className={`w-3 h-3 ${checked ? cfg.ic : "text-slate-400"}`} />{cat}</button>;
                    })}
                  </div>
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    <div className="w-full"><p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Sort</p></div>
                    {sortOptions.map((opt) => <button key={opt.value} onClick={() => onSelectSort(opt.value)} className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold" style={sortBy === opt.value ? { backgroundColor: theme.buttonBg, color: "#fff" } : { backgroundColor: "#f1f5f9", color: "#475569" }} data-testid={`sort-option-${opt.value}`}>{opt.label}</button>)}
                  </div>
                </div>

                {/* Dynamic result label */}
                <div className="text-center pb-2" data-testid="result-label">
                  <p className="text-xs font-medium text-slate-500">{resultLabel}</p>
                </div>
              </div>

              <div className="border-t border-slate-100 px-5 py-4 bg-white space-y-2">
                {dietaryDiffersFromDefaults && dietaryCount > 0 && (
                  <button
                    onClick={async () => {
                      setSavingDefaults(true);
                      const ok = saveDietaryDefaults && await saveDietaryDefaults();
                      setSavingDefaults(false);
                      if (ok) {
                        const { toast } = await import("sonner");
                        toast.success("Saved as your default dietary filters");
                      }
                    }}
                    disabled={savingDefaults}
                    className="w-full py-2.5 rounded-2xl border-2 border-emerald-400 bg-emerald-50 text-emerald-700 text-xs font-bold active:scale-[0.98] transition-transform disabled:opacity-50"
                    data-testid="save-dietary-defaults-btn"
                  >
                    {savingDefaults ? "Saving..." : "Save as my default"}
                  </button>
                )}
                {isDiscover && dietaryCount === 0 ? (
                  <button disabled className="w-full py-3 rounded-2xl bg-slate-300 text-slate-500 text-sm font-bold cursor-not-allowed" data-testid="filters-apply-btn">Select a dietary filter first</button>
                ) : (
                  <button onClick={() => setIsOpen(false)} className="w-full py-3 rounded-2xl text-white text-sm font-bold shadow-lg active:scale-[0.98] transition-transform" style={{ backgroundColor: theme.buttonBg }} data-testid="filters-apply-btn">Show Results</button>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();

  const items = [
    { icon: HomeIcon, label: "Home", path: "/" },
    { icon: Compass, label: "Discover", path: "/discover" },
    { icon: Gift, label: "My Birthday", path: "/my-birthday" },
    { icon: Sparkles, label: "Activity", path: "/activity" },
    { icon: User, label: "Profile", path: "/profile" },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-slate-200 px-2 py-2.5 pb-6 flex justify-around items-end z-[100] shadow-[0_-4px_20px_rgba(0,0,0,0.05)]" data-testid="bottom-nav">
      {items.map((item) => {
        const isActive = item.path === "/"
          ? location.pathname === "/"
          : location.pathname.startsWith(item.path);
        return (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className="relative flex flex-col items-center gap-0.5 min-w-[56px] min-h-[44px] px-2 py-1 rounded-xl transition-colors"
            style={{ color: isActive ? theme.navActive : "#94a3b8" }}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="relative">
              <item.icon className={`w-5 h-5 ${isActive ? "stroke-[2.5]" : ""}`} />
            </div>
            <span className="text-[10px] font-medium leading-tight">{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
};
